import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Table from 'components/Table'
import StickyPagination from 'components/StickyPagination'

import CustomerList from 'pages/shared/CustomerList'
import CustomerSearch from 'pages/Customers/CustomerSearch'
import DeviceLinks from 'pages/Customers/DeviceLinks'
import RemoteConfigSessionsPanel from 'pages/shared/RemoteConfigSessionsPanel'

import usePagedCustomers from 'hooks/usePagedCustomers'
import useSearchParams from 'hooks/useSearchParams'

import GA4 from 'helpers/ga4'

import { PageTitle, SinglePanePage } from 'styles/shared'

const ASC_SORT = 'asc'

const Customers = () => {
  const queryFromUrl = useSearchParams('q')
  const filtersFromUrl = useSearchParams('filters')
  const pageFromUrl = parseInt(useSearchParams('page')) || 1
  const sortAttributeFromUrl = useSearchParams('sortAttribute') || 'customer'
  const sortDirectionFromUrl = useSearchParams('sortDirection') || ASC_SORT

  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now())

  const [customerCount, setCustomerCount] = useState(null)

  const { customersMetadata, arePagedCustomersLoaded } = usePagedCustomers({
    query: queryFromUrl,
    filters: filtersFromUrl,
    page: pageFromUrl,
    sortAttribute: sortAttributeFromUrl,
    sortDirection: sortDirectionFromUrl,
    size: '10',
    refreshTimestamp
  })

  useEffect(() => {
    if (arePagedCustomersLoaded) {
      setCustomerCount(customersMetadata?.filteredItemCount)
    }
  }, [arePagedCustomersLoaded, customersMetadata?.filteredItemCount])

  const navigate = useNavigate()

  const constructUrl = ({ page = pageFromUrl, sortAttribute = sortAttributeFromUrl, sortDirection = sortDirectionFromUrl, query = queryFromUrl, filters = filtersFromUrl }) => (
    `/customers?page=${page}&sortAttribute=${sortAttribute}&sortDirection=${sortDirection}${query ? `&q=${query}` : ''}${filters ? `&filters=${filters}` : ''}`
  )

  const handlePageChange = (p) => {
    navigate(constructUrl({ page: p }))
  }

  return (
    <SinglePanePage>
      <S.Header>
        <PageTitle>{customerCount} Customer{customerCount === 1 ? '' : 's'}</PageTitle>

        <S.Row>
          <S.CustomerSearch query={queryFromUrl} filters={filtersFromUrl} onSearch={(newQuery, newFilters) => { navigate(constructUrl({ query: newQuery, filters: newFilters })) }} showSpinner={!arePagedCustomersLoaded} />
          <DeviceLinks />
        </S.Row>

        <S.RemoteConfigSessionsPanel sendEventCallback={(action) => GA4.sendEvent({ category: 'Customer List Page', action })} />
      </S.Header>

      <CustomerList
        updateRefreshTimestampCallback={() => setRefreshTimestamp(Date.now())}
        noDataMessage={{ title: 'No Customers', description: '' }}
        constructUrlCallback={constructUrl}
      />
      {customersMetadata?.filteredItemCount > 10 && (
        <StickyPagination pageCount={customersMetadata.pageCount} onPageChange={handlePageChange} page={customersMetadata.pageNumber} />
      )}
    </SinglePanePage>
  )
}

export default Customers

const S = {
  Header: styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-bottom: 15px;
  `,
  CustomerSearch: styled(CustomerSearch)`
    margin-bottom: 10px;
    display: inline-block;
    white-space: nowrap;
  `,
  Row: styled.div`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: baseline;
  `,
  ActionTakenCell: styled(Table.Cell)`
    flex-direction: row;
    margin-bottom: -15px;
    padding: 0;
  `,
  RemoteConfigSessionsPanel: styled(RemoteConfigSessionsPanel)`
    margin: 10px 0 5px 0;
  `
}
